// apps/dashboard/src/lib/api.ts
// Fetch wrapper that injects the JWT Authorization header.
// All calls go to /api (proxied by Vite to the API Gateway on port 7000).
// Reads the token directly from localStorage to avoid circular deps with auth store.

const BASE = '/api';
const TOKEN_KEY = 'omniflow:token';
// Auth state key from Zustand persist — must also be cleared on 401 to avoid
// infinite redirect loops (Zustand v5 rehydrates synchronously from localStorage).
const AUTH_STATE_KEY = 'omniflow:auth';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = new Headers(init.headers ?? {});
  // Only set Content-Type for non-FormData bodies — browser sets it automatically
  // for FormData (including the multipart boundary).
  if (!(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401) {
    // If branding fails with 401, we just throw without clearing local session.
    // This allows branding to fail gracefully without logging out the user.
    if (path.includes('/branding/current')) {
      throw new ApiError(401, 'Unauthorized');
    }

    localStorage.removeItem(TOKEN_KEY);
    // Also clear Zustand's persisted auth state so that on page reload Zustand v5
    // rehydrates with isAuthenticated=false and the /login beforeLoad does not
    // immediately redirect back to /inbox (which would create an infinite loop).
    localStorage.removeItem(AUTH_STATE_KEY);
    // Only redirect if we're not already on the login page (avoids hiding error messages)
    if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
      window.location.href = '/login';
    }
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const errField = (body as { error?: unknown })?.error;
    let message: string;
    if (typeof errField === 'string') {
      message = errField;
    } else if (typeof errField === 'object' && errField !== null) {
      // Zod flatten: { formErrors: string[], fieldErrors: Record<string, string[]> }
      const flat = errField as { formErrors?: string[]; fieldErrors?: Record<string, string[]> };
      const msgs = [
        ...(flat.formErrors ?? []),
        ...Object.entries(flat.fieldErrors ?? {}).flatMap(([, errs]) => errs),
      ];
      message = msgs.join('; ') || res.statusText;
    } else {
      message = res.statusText;
    }
    throw new ApiError(res.status, message, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),
};
